import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Sparkles, ArrowRight, Clock, User } from 'lucide-react';
import { useAuth } from '../lib/AuthContext';
import { BookmarkButton } from './BookmarkButton';

const API = process.env.REACT_APP_BACKEND_URL;

export const RecommendedForYou = () => {
  const { user, token } = useAuth();
  const [recs, setRecs] = useState([]);
  const [basedOn, setBasedOn] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user || !token) { setLoading(false); return; }
    fetch(`${API}/api/recommendations`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : { recommendations: [] })
      .then(d => {
        setRecs(d.recommendations || []);
        setBasedOn(d.based_on || []);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [user, token]);

  // Not logged in — show sign-in CTA
  if (!user) {
    return (
      <section className="py-12 bg-[#050505]" data-testid="recommended-section-cta">
        <div className="container-ninja">
          <div className="max-w-2xl mx-auto text-center bg-zinc-900/50 border border-zinc-800 py-10 px-6">
            <Sparkles className="w-6 h-6 text-[#e8ff00] mx-auto mb-4" />
            <h2 className="text-white text-xl mb-2" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>Personalized Recommendations</h2>
            <p className="text-zinc-500 text-sm mb-5">Sign in to get articles tailored to your interests and saved items.</p>
            <Link to="/auth"
              className="inline-flex items-center gap-2 px-6 py-2.5 bg-[#e8ff00] text-black text-xs uppercase tracking-wider hover:opacity-90 transition-opacity"
              style={{ fontFamily: 'Bebas Neue, sans-serif' }}
              data-testid="rec-sign-in-btn">
              <User className="w-3.5 h-3.5" /> Sign In
            </Link>
          </div>
        </div>
      </section>
    );
  }

  if (loading || recs.length === 0) return null;

  return (
    <section className="py-14 md:py-20 bg-[#050505]" data-testid="recommended-section">
      <div className="container-ninja">
        <div className="flex items-center justify-between mb-6">
          <div>
            <p className="text-[#e8ff00] text-xs uppercase tracking-wider mb-1.5 flex items-center gap-2" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>
              <Sparkles className="w-3.5 h-3.5" /> Recommended for You
            </p>
            <h2 className="text-white text-2xl md:text-3xl" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>
              Based on Your {basedOn[0] === 'trending' ? 'Trending' : basedOn.slice(0, 3).join(', ')} Interest{basedOn.length !== 1 ? 's' : ''}
            </h2>
          </div>
          <Link to="/blog" className="hidden sm:flex items-center gap-1 text-zinc-500 text-xs uppercase tracking-wider hover:text-[#e8ff00] transition-colors" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>
            View All <ArrowRight className="w-3 h-3" />
          </Link>
        </div>

        {/* Horizontal scroll row */}
        <div className="flex gap-4 overflow-x-auto pb-4 scrollbar-hide -mx-4 px-4" data-testid="recommended-cards">
          {recs.map(article => (
            <Link
              key={article.slug}
              to={`/blogs/article/${article.slug}`}
              className="group flex-shrink-0 w-72 bg-zinc-900/80 border border-zinc-800 hover:border-[#e8ff00]/30 transition-all overflow-hidden"
              data-testid={`rec-card-${article.slug}`}
            >
              {/* Image */}
              <div className="relative h-40 overflow-hidden bg-zinc-800">
                {article.featured_image ? (
                  <img
                    src={article.featured_image}
                    alt={article.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    loading="lazy"
                  />
                ) : (
                  <div className="w-full h-full bg-gradient-to-br from-zinc-800 to-zinc-900 flex items-center justify-center">
                    <Sparkles className="w-8 h-8 text-zinc-700" />
                  </div>
                )}
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />

                {/* Bookmark */}
                <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
                  <BookmarkButton item={{
                    type: 'article',
                    item_id: article.slug,
                    title: article.title,
                    image: article.featured_image,
                    category: article.category,
                    excerpt: article.excerpt || '',
                  }} />
                </div>

                {/* Category badge */}
                {article.category && (
                  <span className="absolute bottom-2 left-3 text-[10px] uppercase tracking-wider bg-black/70 backdrop-blur-sm text-[#e8ff00] px-2 py-0.5 border border-[#e8ff00]/20"
                    style={{ fontFamily: 'Bebas Neue, sans-serif' }}>
                    {article.category}
                  </span>
                )}
              </div>

              {/* Content */}
              <div className="p-4">
                <h3 className="text-white text-sm font-medium leading-snug line-clamp-2 group-hover:text-[#e8ff00] transition-colors mb-2">
                  {article.title}
                </h3>
                {article.excerpt && (
                  <p className="text-zinc-500 text-xs line-clamp-2 mb-3">{article.excerpt}</p>
                )}
                <div className="flex items-center gap-3 text-zinc-600 text-[10px]">
                  {article.read_time && (
                    <span className="flex items-center gap-1">
                      <Clock className="w-3 h-3" /> {article.read_time} min
                    </span>
                  )}
                </div>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
};
